<template>
  <div />
</template>

<script>
import Process from '@/models/Process';
import Location from '@/models/CustomLocation';
//import { generateFullAddress } from '@/config'

import { mapGetters, mapMutations } from 'vuex';
import { isObject } from '@/services/validation'; // , isNumeric

const uuid = require('uuid');

export default {
  name: 'CustomBaseStep',
  data() {
    return {
      uuid: null,
      disabled: false,

      /**
       * A flag that is briefly set when cancelling a change of an already completed step
       *  It's used to reload the components by briefly changing their visibility
       */
      reloading: false,

      //currentStep: 1,

      /**
       * Whether the form is active.
       *  It's not when the step has already been completed, and the user didn't explicitly activate the form.
       */
      locked: false,

      /**
       * Whether the step details are being corrected (meaning the progress should remain unaffected upon saving data)
       */
      correction: false,

      /**
       * Whether we're saving before going to the next step
       */
      goingToNextStep: false,
      redirectToAfterSubmit: null,

      /**
       *
       */
      feedback: null,
      feedbackMessage: '',
      savedProcess: null,
      savedLocation: null,

      /**
       * Docs = docs belonging to active Location, by doctype
       * RealistionDocs = docs belonging to active Realisation process, by doctype
       */
      Docs: {},
      RealisationDocs: {},
      address: {
        city: 'city',
        number: 'number',
        suffix: 'suffix',
        postalcode: 'postalcode',
        street: 'street'
      },
      lastSavedRequestUuids: [],
      cpoAdvice: null,
      token: null
    };
  },
  computed: {
    ...mapGetters('requests', ['requestByUuid']),
    ...mapGetters('realisations', ['hasRecord', 'recordByUuid']),
    ...mapGetters('relations', ['changeCount', 'requestUuidsByRealisationUuid']),
    ...mapGetters('user', ['hasRole', 'canAccessRequests']),
    ...mapGetters('tenant', ['getWorkflowUuidByName', 'isCurrentTenant', 'getEnabledContractors', 'getSteps']),
    ...mapGetters('chargingpoints', ['getChargingPointByUUID']),
    ...mapGetters('realisation', ['getActiveRecordValues']),

    ...mapGetters('processes', ['getSelectedProcess', 'getSelectedAsset', 'getCurrentProcessType', 'getDisableSaveRecord', 'getActiveProcessTypes']),
    ...mapGetters('assets', ['getAssets']),

    steps () {
      return this.getSteps
    },
    stepsByUuid() {
      return (this.getSteps || []).reduce((result, step) => {
        result[step.uuid] = step;
        return result;
      }, {});
    },
    routeUuid() {
      return this.$route.params.uuid;
    },

    currentProcessType () {
      return this.record.processType?.value
    },

    /**
     * Create a new record if the requested record cannot be found
     */
    record() {
      return this.getSelectedProcess || {
        status: {},
        CurrentLocation: {}
      }
    },

    isNewProcess () {
      return this.$route.name?.startsWith('new')
    },

    /**
     * Whether or not the Process has been saved
     */
    isUnsavedRecord() {
      return (this.record.ref || null) === null;
    },

    hasProcessType() {
      return (this.record.processType || null) !== null;
    },

    /**
     * The active step of the proces
     */
    step() {
      if (!this.record) return 1;
      return this.currentStep
    },

    /**
     * Get the active & current step data from the configuration
     */
    currentStepData() {
      this.$store.dispatch('realisations/updateCurrentStep', this.currentStep);
      return this.getStepDataByNumber({ number: this.currentStep }) || {};
    },

    /**
     * The location details
     */
    values() {
      return this.getSelectedProcess?.CurrentLocation || { location: { address: {}, chargingStation: {} } }
    },

    CurrentLocation() {
      return this.values;
    },

    isSteplessProcess () {
      return ['private', 'brussels', 'wallonia', 'takeover'].includes(this.currentProcessType)
    },

    /**
     * Location version
     */
    version() {
      try {
        return this.values.version;
      } catch (e) {
        return 1;
      }
    },

    activeProcessStep () {
      return this.record.processStatus?.currentStep
    },

    currentStep () {
      const step = this.record.processStatus?.currentStep
      const finalStep = this.getSteps?.at(-1)?.step
      const currentStep = (step === 100 ? finalStep : step) || 1

      return +this.$route.query?.step || currentStep
    },

    /**
     * The related requests UUIDS
     */
    requestUuids() {
      return this.requestUuidsByRealisationUuid({ uuid: this.record.uuid });
    },

    /**
     * The related request objects
     */
    connectedRequests() {
      return this.requestUuids.slice().map(uuid => {
        return this.requestByUuid({ uuid });
      });
    },

    /**
     * Whether the step can still be changed
     */
    openForChange() {
      return (
        this.record.status.completed === false &&
        this.record.status.cancelled !== true &&
        this.record.status.onhold !== true
      );
    },

    /**
     * Whether the CPO Advice should be / was skipped
     *  This is due to the current location being suggested by the CPO
     */
    Skip() {
      return !!this.values.CPOAdvice.Skip;
    },
    completed() {
      return !!this.record.status.completed;
    },
    statusLocked() {
      return !!this.record.status.locked;
    },
    onhold() {
      return !!this.record.status.onhold;
    },
    cancelled() {
      return !!this.record.status.cancelled;
    },
    cancelledOrOnHold() {
      return this.onhold || this.cancelled;
    },
    disableNextBtn() {
      return this.cpoAdvice === 'no' ||
             (this.cancelledOrOnHold && !this.completedUuids.includes(this.currentStepData.uuid));
    },

    userIdentity() {
      const identity = this.$auth.user?.['https://evtools.nl/identity']
      return identity?.uuid ? identity : {}
    },

    /**
     * Access
     */
    mayEdit() {
      return !this.statusLocked && (
        this.hasRole({ role: 'admin' }) ||
        (this.currentStepData?.access?.edit ?? []).some(role => this.hasStepRole(role))
      );
    },

    /**
     * Record Flow State
     */

    recordState() {
      try {
        return this.record.flow.state;
      } catch (e) {
        return [];
      }
    },
    completedUuids() {
      return this.recordState.filter(state => state.completed_at).map(state => state.uuid);
    },
    availableUuids() {
      return this.recordState.filter(state => state.started_at).map(state => state.uuid);
    },
    isCompletedStep() {
      //console.log('processFlowMixin', !!this.processFlowMixin(), 'mapMixin', !!this.mapMixin())
      if (this.processFlowMixin()) {
        return this.processFlowMixin().isCompletedStep
      }
      return this.completedUuids.includes(this.currentStepData?.uuid);
    },
    isAvailableStep() {
      return this.availableUuids.includes(this.currentStepData?.uuid);
    },
    realisationLocationData () {
      return this.record.realisationProcessData?.process || {}
    }
  },
  watch: {
    routeUuid(newValue) {
      if (newValue !== null) {
        this.uuid = newValue;
      }
      this.clearChanges();
    },
    hasRecord() {
      if (!this.hasRecord) {
        this.redirectToStart();
      }
    },
    /**
     * When the record changes we check whether the form should be active
     */
    record () {
      if (this.hasRecord) {
        this.locked = !this.mayEdit || this.isCompletedStep || this.completed || this.onhold || this.cancelled;
        this.RealisationDocs = this.record.Docs;
      }
    },
    changeCount: {
      handler() {
        if (this.changeCount !== 0) {
          this.storeRelationChanges({ record: {} });
        }
      }
    },
    getSelectedProcess: {
      deep: true,
      handler (process) {
        if (process) {
          const data = process.CurrentLocation || process.values.Location
          // For correction/undo
          const values = new Location({ data })
          this.$store.dispatch('realisation/setSavedRecordValues', { values });
        }
      }
    },
    getSelectedAsset: {
      deep: true,
      immediate: true,
      handler (asset) {
        if (!this.record?.ref || this.openForChange) return

        if (asset?.processes) {
          const latestAssetProcess = asset.processes.at(0)
          const isLocked = latestAssetProcess.processUuid !== this.$route.params.uuid

          this.getSelectedProcess.status.locked = this.record.status.locked = isLocked
          this.$store.dispatch('processes/setSelectedProcess', { process: this.getSelectedProcess })
        }
      }
    },
    currentStepData: {
      deep: true,
      immediate: true,
      handler () {
        this.locked = !this.mayEdit || this.isCompletedStep || this.completed || this.onhold || this.cancelled;
      }
    },
    getDisableSaveRecord: {
      handler (value) {
        this.disabled = value
      }
    },
    locked: {
      immediate: true,
      handler (locked) {
        this.locked = locked
        this.$store.dispatch('processes/setRecordLocked', { locked })
      }
    }
  },
  async created() {
    this.uuid = this.routeUuid || 'new';

    const values = JSON.parse(JSON.stringify(this.record.CurrentLocation))
    this.$store.dispatch('realisation/setSavedRecordValues', { values })
    this.token = await this.$auth.getTokenSilently()
  },
  beforeDestroy() {
    this.clearChanges();
  },
  methods: {
    ...mapMutations('relations', ['setConnectionChange', 'finalizeChanges', 'clearChanges']),
    ...mapMutations('chargingpoints', ['replaceLocation', 'resetLocationStatus']),

    hasStepRole(role) {
      if (!role.includes(':')) return this.hasRole({ role });
      const [category, partyRole] = role.split(':');
      return (this.record?.stakeholders ?? []).some(s =>
        s.category === category &&
        s.uuid === this.userIdentity?.uuid &&
        s.partyRole === partyRole
      );
    },

    getStepDataByNumber({ number }) {
      const matches = (this.getSteps || []).filter(step => (step.step || step.short) == number)
      if (matches.length <= 1) return matches[0]
      return matches.find(s => this.availableUuids.includes(s.uuid)) || matches[0]
    },

    processFlowMixin () {
      return this.$children.find(mixin => mixin.$vnode.tag.includes('ProcessFlow'))
    },

    activeMixin () {
      return this.mapMixin() || this.processFlowMixin()
    },

    mapMixin () {
      return this.$children.find(mixin => mixin.$vnode.tag.includes('CustomProcessMap'))?.$refs?.customProcessMap ||
             this.$children.find(mixin => mixin.$vnode.tag.includes('CustomMapLocation'))
    },

    async saveRecord () {
      if (this.processFlowMixin()) {
        // const form = this.processFlowMixin().$refs.form
        // form.validate()
        // if (!form.isValid()) {
        //   this.handleValidationError();
        //   return;
        // }

        await this.processFlowMixin().handleSubmit()
      } else {
        this.mapMixin().handleSubmit()
      }
    },

    redirectToStart() {
      if (this.currentStep === 1) return; // Already there...
      const record = this.getSelectedProcess //this.recordByUuid({ uuid: this.uuid });
      if (record) {
        this.$router.push({
          name: `${this.currentProcessType}Step1`,
          params: {
            uuid: this.uuid,
            step: 1
          }
        });
      }
    },
    handleNext() {
      if (this.processFlowMixin()) {
        return this.processFlowMixin().handleNext()
      }
      if (this.record.ref) {
        const stepState = this.record.nextAvailableFlowStep({ currentUuid: this.currentStepData.uuid });
        this.redirectToStep({ step: this.stepsByUuid[stepState.uuid].short });
      }
    },
    handlePrevious() {
      if (this.processFlowMixin()) {
        return this.processFlowMixin().handlePrevious()
      }

      // TODO: currentStep is no longer a number...
      let stepState = this.record.previousAvailableFlowStep({ currentUuid: this.currentStepData.uuid });
      this.redirectToStep({ step: this.stepsByUuid[stepState.uuid].short });
      // this.redirectToStep({ step: this.currentStep - 1 })
    },
    redirectToStep({ step }) {
      const record = this.getSelectedProcess //this.recordByUuid({ uuid: this.uuid });
      if (record) {
        const type = this.getSelectedProcess.processType.routeType
        this.$router.replace({
          name: `${this.currentProcessType}Steps`,
          params: { type, uuid: this.uuid },
          query: { step }
        });
      }
    },

    handleInput({ name, value }) {
      const path = name.split('.'),
            last = path.pop(),
            currentLocation = this.getSelectedProcess?.CurrentLocation || this.getActiveRecordValues

      const isNumber = ['latitude', 'longitude'].includes(last)
      const val = isNumber ? Number(value) : value

      path.reduce((o, k) => (o[k] = o[k] || {}), currentLocation)[last] = val;

      if (!this.isNewProcess) {
        this.getSelectedProcess.CurrentLocation = currentLocation
        this.$store.dispatch('processes/setSelectedProcess', { process: this.getSelectedProcess })
        this.$store.dispatch('realisation/setActiveRecordValues', { values: currentLocation })
      }

      this.$emit('handleInput', { name, val })
    },
    /**
     * Submit and go to the next step
     */
    async handleSubmitAndNext() {
      // FlowInteraction component is moved from Step components to a Process comp. //
      // The $refs form is nested now //
      if (this.mapMixin()) {
        const mapForm = this.mapMixin().$refs.mapLocationForm
        mapForm.validate()
        if (!mapForm.isValid()) {
          this.handleValidationError();
          return;
        }
      }

      if (this.processFlowMixin()) {
        return this.processFlowMixin().handleSubmitAndNext()
      }

      // if (this.mapMixin()) {
      //   return this.mapMixin().handleSubmitAndNext()
      // }
      //console.log('BASESTEP', 'form', this.$refs.form, 'mapMixin', this.mapMixin(), 'processFlowMixin', this.processFlowMixin())
      const form = this.$refs.form
      if (form.validate) {
        form.validate();

        if (!form.isValid()) {
          this.handleValidationError();
          return;
        }
      }

      this.goingToNextStep = true;
      let success = await this.handleSubmit();
      this.goingToNextStep = false;

      if (success) {
        if (this.redirectToAfterSubmit) {
          this.redirectToStep({ step: this.redirectToAfterSubmit });
        } else {
          this.handleNext();
        }
      }
    },

    async handleComplete () {
       if (this.mapMixin()) {
        const mapForm = this.mapMixin().$refs.mapLocationForm
        mapForm.validate()
        if (!mapForm.isValid()) {
          this.handleValidationError();
          return;
        }
      }

      if (this.processFlowMixin()) {
        return this.processFlowMixin().handleComplete()
      }

      this.goingToNextStep = true
      let success = await this.handleSubmit()
      this.goingToNextStep = false

      if (success) {
        this.locked = true
        this.$router.replace({ name: 'LocationList', params: { locationId: this.getSelectedAsset.locationId } })
      }
    },

    /**
     * Handle Validation Error
     */
    handleValidationError() {
      this.feedback = 'danger';
      this.feedbackMessage = this.$t('BaseRealisationStep.validation.checkAllFields');

      this.$emit('feedback', { feedback: this.feedback, message: this.feedbackMessage })
    },

    handleFileChanges({ doctype, files }) {
      this.Docs[doctype] = files
    },

    /**
     * Stub
     */
    prepIntention({ step, correction, complete, target }) {
      return { step, correction, complete, target };
    },

    /**
     * Stub
     */
    prepData({ data }) {
      return data;
    },

    /**
     * Stub
     */
    isValidData() {
      return { valid: true };
    },

    prepStatus() {
      // { data }
      // TODO: Maybe remove this method, now that this status has been replaced with Intention?
      return {};
    },

    postSave() {},

    mergeLocationData({ data }) {
      data.Location = this.CurrentLocation.Location;
      return data;
    },

    async changeChargingpointStatus({ response }) {
      const uuid = response.data.record.process?.Location?.chargingpointUuid;
      await this.$store.dispatch('chargingpoints/changeChargingpointStatus', {
        uuid,
        status: 'realized' // Step 8 completed
      });
    },

    /**
     * Handle Submit event
     */
    async handleSubmit() {
      try {
        let intention = this.prepIntention({
          step: this.currentStepData.uuid,
          correction: this.correction,
          complete: this.goingToNextStep,
          target: null // TODO: implement?
        });

        // console.log("submit")

        this.feedback = null;
        this.feedbackMessage = '';
        this.disabled = true;

        let data = this.getSelectedProcess?.CurrentLocation || this.getActiveRecordValues
        data.location = data.location?.location || data.location

        data = this.prepData({ data });

        // Version
        data.version = this.version || 1;
        intention.target = data.apiTargetStep

        if (this.requestUuids?.length) {
          data.requestUuids = this.requestUuids;
        }
        if (this.getSelectedProcess?.locationId) {
          data.locationId = this.getSelectedProcess.locationId
        }

        if (this.record.realisationProcessData) {
          data.realisationProcessData = this.record.realisationProcessData
        }

        // Add possible (under corr.) docs to payload //
        Object.keys(this.Docs).forEach(key => data.Docs[key] = this.Docs[key])

        // Get the record reference, or add the uuid in case of a new record
        const ref = this.record.ref || false;
        if (ref === false) {
          if (this.getSelectedAsset) {
            // All asset processes need to be completed prior starting a new  //
            const activeAssetProcess = this.getSelectedAsset.processes.at(0)
            const canStartNewProcess = activeAssetProcess.isCompleted || activeAssetProcess.processStatus?.status === 'cancelled'
            if (!canStartNewProcess) {
              return this.$store.dispatch('tenant/setToast', {
                vm: this,
                message: 'Er is al een actief proces op de gekozen locatie!',
                error: true,
                delay: 100
              });
            }
          }

          data.uuid = uuid.v4();
          //data.category = this.record.realisationCategory || 'request'
        }

        data.step = this.currentStepData.uuid;
        data.processType = this.getActiveProcessTypes.find(p => p.value === this.getCurrentProcessType)

        data.status = this.prepStatus({ data });
        data.correction = this.correction;

        const validStepData = this.isValidData({ ref, data })
        if (!validStepData.valid) {
          // console.log("invalid data")
          // Note: The validation method should set the feedback
          this.$emit('feedback', validStepData)
          this.disabled = false;
          this.$store.dispatch('processes/isSavingProcess', false)
          return false;
        }
        // Ensure that the coordinates are not strings //
        if (data.location?.address?.latitude) {
          const { latitude, longitude } = data.location.address
          data.location.address.latitude = +latitude
          data.location.address.longitude = +longitude
        }

        // Temp: Generate full address field for Mongo indexing //
        // if (data.Location?.StreetName) {
        //   // ensure house number, lat and long are numbers for validation
        //   data.Location.HouseNumber = Number(data.Location.HouseNumber)
        //   data.Location.Latitude = Number(data.Location.Latitude)
        //   data.Location.Longitude = Number(data.Location.Longitude)
        //   const code = data.Municipality
        //   data.Location.FullAddress = generateFullAddress({ location: data.Location, code })
        // }

        this.$emit('isSaving', true)
        this.$store.dispatch('processes/isSavingProcess', true)

        // console.log('BASE', data, 'ref', this.record.ref, 'currentStep', this.currentStep, this.record.CurrentLocation, this.record)
        // console.log('handleSubmit', 'processflowmixin', this.processFlowMixin())
        // console.log('data', data)
        // if (this.record.ref !== 'sarma') {
        //   this.disabled = false
        //   this.$store.dispatch('processes/isSavingProcess', false)
        //   this.$emit('isSaving', false)
        //   return
        // }

        // set the proper endpoint
        let endpoint = '/api/process_step';
        if (!ref) {
          endpoint = '/api/process_create';

          data.flowUuid = this.getWorkflowUuidByName({
            name: 'realisation'
          });
        }

        /**
         * This is to 'upgrade' existing realisation processes
         */
        // if (!this.record.getFlowUuid()) {
        //   data.flowUuid = this.getWorkflowUuidByName({
        //     name: 'realisation'
        //   });
        // }

        // Make the call
        const api = await fetch(endpoint, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.token}`
          },
          body: JSON.stringify({
            ref,
            data,
            intention
          })
        }).catch(error => {
          console.log('error: ', error);
        });
        if (!api.ok) {
          if (api.status === 401) {
            this.feedbackMessage =
              'U heeft niet de rechten om deze handeling uit te voeren. Mogelijk is uw sessie verlopen.';
          }
          if (api.status === 400) {
            this.feedbackMessage = 'De invoer is ongeldig verklaard. Neem contact op indien dit zich blijft voordoen.';
          }
          this.$emit('isSaving', false)
          this.$store.dispatch('processes/isSavingProcess', false)

          throw new Error(api.statusText);
        }
        const response = await api.json();

        if (!response.data.record.realisationProcessData && this.getSelectedProcess?.realisationProcessData) {
          response.data.record.realisationProcessData = this.getSelectedProcess.realisationProcessData
        }

        const process = new Process({ data: response.data.record, ref: response.data.record._id });
        this.$store.dispatch('processes/setSelectedProcess', { process, copy: true })

        this.$emit('currentRecord', process)
        this.$emit('feedback', { feedback: null, message: '' })

        const asset = response.data.asset
        if (asset) {
          this.$store.dispatch('processes/setSelectedAsset', { asset })

          if (this.getAssets) {
            this.$store.dispatch('assets/replaceAsset', { asset })
          }
        }

        if (!ref) {
          const [linkedChargingpoint] = response.data.locations;
          if (linkedChargingpoint) {
            if (!this.getChargingPointByUUID({ uuid: linkedChargingpoint.uuid })) {
              await this.$store.dispatch('chargingpoints/putCreatedChargingpoint', linkedChargingpoint);
            }
          }
          this.uuid = process.uuid

          setTimeout(() => {
            this.$router.replace({
              name: `${process.processType.value}Steps`,
              params: { type: process.processType.routeType, uuid: process.uuid },
              query: { step: 1 }
            })
          }, 100)

          // if (!this.goingToNextStep) {
          //   this.redirectToStep({ step: '1' });
          // }
        }
        // Process list
        this.$store.dispatch('processes/replaceProcess', { process: response.data.record })

        if (Array.isArray(response.data.locations)) {
          response.data.locations.forEach(location => {
            this.replaceLocation({ location });
          });
        }

        if (this.record.status.completed && !this.correction) {
          this.changeChargingpointStatus({ response });
        }

        this.$store.dispatch('tenant/setToast', {
          vm: this,
          message: this.$i18n.t('Process.processSaved'),
          error: false,
          delay: 100
        });

        this.postSave();
        this.canAddMapLocation = false;
        this.disabled = false;
        this.correction = false
        this.$emit('handleCancelChange', true)
        return true;
      } catch (e) {
        console.log(e, e.message);

        this.disabled = false;
        this.feedback = 'danger';
        this.feedbackMessage = this.feedbackMessage || e.message || this.$i18n.t('Process.generalSaveError');
        return false;
      }
    },
    /**
     * Add / Remove Requests to Realisation
     */
     async storeRelationChanges ({ record }) {
      try {
        const ref = this.record.ref || record.ref || false;

        if (!this.canAccessRequests) return;

        this.feedback = null;
        this.feedbackMessage = '';

        if (!ref) return;

        const data = {
          step: 'relations',
          requestUuids: this.requestUuids
        };

        // Make the call
        const api = await fetch('/api/realisation_relations', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.token}`
          },
          body: JSON.stringify({
            ref,
            data
          })
        });
        if (!api.ok) {
          // console.log(api.status)
          if (api.status === 401) {
            this.feedbackMessage =
              'U heeft niet de rechten om deze handeling uit te voeren. Mogelijk is uw sessie verlopen.';
          }
          if (api.status === 400) {
            this.feedbackMessage = 'De invoer is ongeldig verklaard. Neem contact op indien dit zich blijft voordoen.';
          }
          // console.log(api)
          throw new Error('Unable to store connection');
        }
        const response = await api.json();

        this.finalizeChanges();
        this.clearChanges();

        const requestUuids = response.data.record.requestUuids
        this.$store.dispatch('processes/setRelatedRequests', { requestUuids })
        // Add possible request's tags to the process
        if (response.data.record.Tags) {
          const tags = response.data.record.Tags
          this.$store.dispatch('processes/setProcessTags', { tags })
        }
      } catch (e) {
        console.log(e);
        this.feedback = 'danger';
        this.feedbackMessage =
          this.feedbackMessage || 'De aanvraag kon niet verbonden worden. Probeer het nog een keer.';
        this.clearChanges();
      }
    },
    addRequestsToRealisation() {
      if (this.record && this.requestAdditions) {
        this.requestAdditions.forEach(uuid => {
          const data = {
            type: 'add',
            requestUuid: uuid,
            realisationUuid: this.record.uuid
          }
          this.$store.dispatch('relations/setRelation', { data })
          this.setConnectionChange({ data });
        });
      }
    },
    handleBreakConnection({ uuid }) {
      const data = {
        type: 'remove',
        requestUuid: uuid,
        realisationUuid: this.record.uuid
      }
      this.$store.dispatch('relations/setRelation', { data })
      this.setConnectionChange({ data });
    },
    handleStartChange() {
      this.savedLocation = new Location({
        data: JSON.parse(JSON.stringify(this.getSelectedProcess.raw.process)),
        processType: this.getSelectedProcess.processType
      })

      this.locked = false;
      if (this.processFlowMixin()) {
        // Only if not realized //
        ['processFlowMixin', 'mapMixin'].forEach(mixin => {
          if (this[mixin]()) {
            this[mixin]().locked = this.activeProcessStep === 100
          }
        })
      }
    },
    handleCancelChange() {
      this.$nextTick(() => {
        if (this.activeProcessStep !== +this.$route.query?.step) {
          this.locked = true;
        }
        this.correction = false
        this.changeStarted = false
        if (!this.isNewProcess) {
          this.$emit('isLocationConnected', true);
        }
        // TODO: Three separate mixins: as sson as possible, move all this to store //
        if (this.processFlowMixin()) {
          ['processFlowMixin', 'mapMixin'].forEach(mixin => {
            if (this[mixin]()) {
              this[mixin]().locked = false
              this[mixin]().correction = false
              this[mixin]().changeStarted = false
              this[mixin]().handleCancelChange();
            }
          })
        }

        if (this.savedLocation) {
          //this.locked = true;
          this.correction = false;
          this.getSelectedProcess.CurrentLocation = this.savedLocation

          this.$store.dispatch('processes/setSelectedProcess', { process: this.getSelectedProcess })
        }

        this.feedback = ''
        this.feedbackMessage = ''
      });
    },
    handleStartCorrection() {
      if (this.processFlowMixin()) {
        this.processFlowMixin().handleStartChange();
        ['processFlowMixin', 'mapMixin'].forEach(mixin => { const m = this[mixin](); if (m) m.correction = true })
      }

      this.handleStartChange();
    },

    /**
     * Docs
     */
    minIdByDocType({ doctype, document }) {
      let docs = document || this.RealisationDocs[doctype] || null;

      if (!Array.isArray(docs)) return 0;

      return docs.reduce((max, doc) => {
        if (isObject(doc) && doc.id) {
          return Math.max(max, doc.id);
        } else {
          return Math.max(max, doc);
        }
      }, 0);
    },
    prepDocData({ doctype }) {
      let docs = this.Docs[doctype] || [];

      return docs
        .filter(file => file.progress === 100)
        .map(file => {
          return {
            id: file.id,
            ext: file.ext
          };
        });
    },
    isBeyondCurrentStep() {
      let uuid = this.currentStepData.uuid;
      return !this.availableUuids.includes(uuid);
    }
  }
};
</script>

<style lang="scss">
.RealisationDetail {
  text-align: left;
  padding: 18px 0 30px;

  &__map {
    position: relative;
    max-width: 100%;
    height: 100%;
  }

  &__aside {
    position: sticky !important;
    top: $menu-height;
  }
  &__RequestList {
    list-style: none;
    margin: 0;
    padding: 0;

    a {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 380px;
    }
    .icon {
      cursor: pointer;

      &:hover {
        color: #ec0000;
      }
    }
  }
}
</style>
